import { $wgsl } from "../../wgsl-preprocessor/wgsl-preprocessor";
import { UniformBuffer } from "../UniformBuffer";
import { initTexture, createSamplerDesc, createSamplerKey, syncGpuSampler } from "../Texture";
import { MipmapPipeline } from "./MipmapPipeline";
import materialUniforms from "./material_uniforms.wgsl";
import { getMaterialHash } from "../Pipelines";

function makeTextureBinding(binding = 0, visibility = GPUShaderStage.FRAGMENT, sampleType = "float") {
  return {
    binding,
    visibility,
    texture: {
      sampleType
    }
  };
}

function makeSamplerBinding(binding = 0, visibility = GPUShaderStage.FRAGMENT) {
  return {
    binding,
    visibility,
    sampler: {}
  };
}

export function getMaterialUniformsDeclaration(bindGroup = 0) {
  return $wgsl(materialUniforms, { bindGroup });
}

export const MaterialUniformFlags = {
  NO_UNIFORMS: 0x10000000,

  TEXTURE_MASK: 0x000000ff,
  MAP_DIFFUSE: 1,
  MAP_SPECULAR: 2,
  MAP_BUMP: 4,
  MAP_NORMAL: 8,
  MAP_ALPHA: 16,

  // Bump/normal invert intentionally omitted — WebGL never honored it either.
  INVERT_DIFFUSE: 1 << 24,
  INVERT_ALPHA: 1 << 27
};

/**
 * The texture map slots handled by MaterialUniforms, in binding order. The texture lives at
 * `binding`, its sampler at `binding + 1`. bumpMap and normalMap intentionally share binding 5 —
 * only one of them is ever active at a time (see initFromMaterial).
 *
 * Single source of truth for getMaterialTextureMask(), bindGroupNeedsUpdate() and
 * syncTextureBindings() so the list doesn't have to be kept in sync in several places.
 */
const TEXTURE_MAPS = [
{ name: 'map', bit: MaterialUniformFlags.MAP_DIFFUSE, binding: 1 },
{ name: 'specularMap', bit: MaterialUniformFlags.MAP_SPECULAR, binding: 3 },
{ name: 'bumpMap', bit: MaterialUniformFlags.MAP_BUMP, binding: 5 },
{ name: 'normalMap', bit: MaterialUniformFlags.MAP_NORMAL, binding: 5 },
{ name: 'alphaMap', bit: MaterialUniformFlags.MAP_ALPHA, binding: 7 }];


const _tmpTexMatrix = new Float32Array(9);

export class MaterialUniforms extends UniformBuffer {

  #device;
  #defaultTexture;
  #layout;
  #bindGroup;
  #mipmapPipeline;

  // Remember which resources we used on last createBindGroup() call, so
  // that we can detect if a texture was replaced. Indexed by binding.
  #boundResourceViews = [];
  #boundSamplerKeys = [];

  constructor(device, material, defaultTexture) {
    // buffer size in floats
    let bufferSize = 40; // texture uniforms
    super(device, bufferSize, true, false, 'material-uniforms');

    this.#device = device;
    this.#defaultTexture = defaultTexture;

    this.#layout = device.createBindGroupLayout({
      label: "material bind group layout",
      entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.FRAGMENT | GPUShaderStage.VERTEX,
        buffer: {}
      },
      makeTextureBinding(1),
      makeSamplerBinding(2),
      makeTextureBinding(3),
      makeSamplerBinding(4),
      makeTextureBinding(5),
      makeSamplerBinding(6),
      makeTextureBinding(7),
      makeSamplerBinding(8)]

    });

    if (material) {
      this.#mipmapPipeline = new MipmapPipeline(device);
      this.initFromMaterial(material);
    } else {
      this.#initEmpty();
    }
  }

  #initEmpty() {

    let view = this.#defaultTexture.view;
    let smpl = this.#defaultTexture.sampler;

    this.#bindGroup = this.#device.createBindGroup({
      layout: this.#layout,
      entries: [
      {
        binding: 0,
        resource: {
          buffer: this.getBuffer()
        }
      },
      { binding: 1, resource: view },
      { binding: 2, resource: smpl },
      { binding: 3, resource: view },
      { binding: 4, resource: smpl },
      { binding: 5, resource: view },
      { binding: 6, resource: smpl },
      { binding: 7, resource: view },
      { binding: 8, resource: smpl }]

    });


  }

  #setTexTransforms(texture, uOffset) {

    let offset = texture.offset;
    let repeat = texture.repeat;

    if (texture.matrix) {
      _tmpTexMatrix.set(texture.matrix.elements);
    } else {
      for (let i = 0; i < 9; i++)
      _tmpTexMatrix[i] = i % 4 ? 0 : 1;
    }

    _tmpTexMatrix[6] += offset.x;
    _tmpTexMatrix[7] += offset.y;
    _tmpTexMatrix[0] *= repeat.x;
    _tmpTexMatrix[3] *= repeat.x;
    _tmpTexMatrix[1] *= repeat.y;
    _tmpTexMatrix[4] *= repeat.y;

    return this.setMatrix3x3IfChanged(uOffset, _tmpTexMatrix);
  }


  /**
   * Make sure that uniform buffer is up to date. We only upload on actual changes
   * to make sure that we only re-upload if the material changes really require it.
   * @param {THREE.Material} material
   * @returns {boolean} true if the uniform buffer was re-uploaded
   */
  updateUniformBuffer(material, forceUpload = false) {

    let needsUpload = false;

    let texForMatrix = material.map || material.specularMap;
    if (texForMatrix) {
      needsUpload |= this.#setTexTransforms(texForMatrix, 0);
    }

    let texForBumpMatrix = material.normalMap || material.bumpMap;
    if (texForBumpMatrix) {
      needsUpload |= this.#setTexTransforms(texForBumpMatrix, 12);
    }

    let texForAlphaMatrix = material.alphaMap;
    if (texForAlphaMatrix) {
      needsUpload |= this.#setTexTransforms(texForAlphaMatrix, 24);
    }

    if (material.bumpMap) {
      needsUpload |= this.setFloatIfChanged(36, material.bumpScale);
      needsUpload |= this.setFloatIfChanged(37, material.bumpScale);
    }

    if (material.normalMap) {
      needsUpload |= this.setFloatIfChanged(36, material.normalScale.x);
      needsUpload |= this.setFloatIfChanged(37, material.normalScale.y);
    }

    if (needsUpload || forceUpload) {
      this.upload();
    }

    return needsUpload;
  }

  initFromMaterial(material) {

    let mapsHash = 0;

    let entries = [
    {
      binding: 0,
      resource: {
        buffer: this.getBuffer()
      }
    }];


    this.#boundResourceViews.fill(null);
    this.#boundSamplerKeys.fill(null);

    const addMap = (name, bit, binding) => {
      let mapView, mapSampler;

      let texture = material[name];
      if (texture) {
        initTexture(this.#device, this.#mipmapPipeline, texture);
        mapView = texture.__gpuTextureView;
        mapSampler = texture.__gpuSampler;
        mapsHash |= bit;
      } else {
        mapView = this.#defaultTexture.view;
        mapSampler = this.#defaultTexture.sampler;
      }

      // track which texture views are used in the bindGroup
      this.#boundResourceViews[binding] = mapView;
      this.#boundResourceViews[binding + 1] = mapSampler;
      // initTexture() above synced the sampler and refreshed __gpuSamplerKey, so reuse it
      // (paired with mapSampler) instead of recomputing the descriptor + key.
      this.#boundSamplerKeys[binding + 1] = texture ? texture.__gpuSamplerKey : null;

      entries.push({ binding: binding, resource: mapView });
      entries.push({ binding: binding + 1, resource: mapSampler });
    };

    addMap("map", MaterialUniformFlags.MAP_DIFFUSE, 1);
    addMap("specularMap", MaterialUniformFlags.MAP_SPECULAR, 3);
    if (material.bumpMap) {
      addMap("bumpMap", MaterialUniformFlags.MAP_BUMP, 5);
    } else {
      addMap("normalMap", MaterialUniformFlags.MAP_NORMAL, 5);
    }
    addMap("alphaMap", MaterialUniformFlags.MAP_ALPHA, 7);

    this.updateUniformBuffer(material, true);

    const bindGroupDesc = {
      layout: this.#layout,
      entries
    };
    this.#bindGroup = this.#device.createBindGroup(bindGroupDesc);

    return mapsHash;
  }

  bindGroupNeedsUpdate(material) {

    // Detect if the bindGroup entry for a map type needs to change
    const mapEntryNeedsUpdate = (name, binding) => {
      // get TextureView that will be used for this map type
      const newTexture = material[name];
      const newTextureView = newTexture?.__gpuTextureView;
      // Derived from the live texture params rather than the cached __gpuSamplerKey: this is
      // also called from onMaterialChanged before the samplers have been (re)synced, where the
      // cached key could still be stale.
      const newSamplerKey = newTexture ?
      createSamplerKey(createSamplerDesc(newTexture)) :
      null;

      // old TextureView
      const oldTextureView = this.#boundResourceViews[binding];
      const oldSamplerKey = this.#boundSamplerKeys[binding + 1];

      // If any of the views will change, the bindGroup will change - which requires
      // an invalidation of any affected RenderBundles.
      return oldTextureView !== newTextureView || oldSamplerKey !== newSamplerKey;
    };

    return TEXTURE_MAPS.some(({ name, binding }) => {
      // bumpMap and normalMap share binding 5; when both are set, bumpMap wins (matches
      // initFromMaterial). Without this, normalMap would always mismatch #boundResourceViews[5]
      // (which holds bumpMap's view), spuriously reporting an update on every check.
      if (name === 'normalMap' && material.bumpMap) {
        return false;
      }
      return mapEntryNeedsUpdate(name, binding);
    });
  }

  getLayout() {
    return this.#layout;
  }

  getBindGroup() {
    return this.#bindGroup;
  }

  /**
   * Upload pending textures and rebuild the bind group when sampler or view changed.
   * @param {THREE.Material} material
   * @returns {boolean} true if the bind group was rebuilt
   */
  syncTextureBindings(material) {
    let needsRebuild = false;
    for (const { name, binding } of TEXTURE_MAPS) {
      const texture = material[name];
      if (!texture) {
        continue;
      }
      // bumpMap and normalMap share binding 5; when both are set, bumpMap wins (matches
      // initFromMaterial). Without this, syncing normalMap here would flag needsRebuild
      // every draw, since #boundResourceViews[5] holds bumpMap's view.
      if (name === 'normalMap' && material.bumpMap) {
        continue;
      }
      if (texture.needsUpdate === true) {
        initTexture(this.#device, this.#mipmapPipeline, texture);
      } else {
        syncGpuSampler(this.#device, texture);
      }
      // Both branches above leave __gpuTextureView and __gpuSamplerKey up to date, so compare
      // against what's currently bound directly - no second createSamplerKey() computation
      // (unlike bindGroupNeedsUpdate(), which must derive the key live for the onMaterialChanged
      // path where the samplers haven't been synced yet).
      if (this.#boundResourceViews[binding] !== texture.__gpuTextureView ||
      this.#boundSamplerKeys[binding + 1] !== texture.__gpuSamplerKey) {
        needsRebuild = true;
      }
    }
    if (!needsRebuild) {
      return false;
    }
    this.initFromMaterial(material);
    return true;
  }

}

export function getMaterialTextureMask(material) {

  let mask = 0;

  for (const { name, bit } of TEXTURE_MAPS) {
    if (material[name]) mask |= bit;
  }

  return mask;
}

function materialDisposeHandler(event) {
  const material = event.target;
  if (material.__gpuMaterialUniforms) {
    material.__gpuMaterialUniforms.getBuffer().destroy();
    // TODO: Do we need to destroy more resources? Textures?
    material.__gpuMaterialUniforms = undefined;
  }

  // Drop the texture 'update' listeners added by syncTextureUpdateListeners.
  const handler = material.__gpuTextureUpdateHandler;
  if (handler && material.__gpuListenedTextures) {
    for (const texture of material.__gpuListenedTextures) {
      texture.removeEventListener('update', handler);
    }
    material.__gpuListenedTextures = null;
  }

  material.removeEventListener('dispose', materialDisposeHandler);
}

// We need one per Renderer instance, because they use separate RenderBundles.
let materialTextureUpdateCallbacks = [];
export function addMaterialTextureUpdateCallback(callback) {
  materialTextureUpdateCallbacks.push(callback);
}

function notifyMaterialTextureUpdate(material) {
  // Forward to all callbacks (one per Renderer instance) so affected RenderBundles are invalidated.
  for (const callback of materialTextureUpdateCallbacks) {
    callback({ target: material });
  }
}

/**
 * Keep the material subscribed to the 'update' event of exactly its current textures.
 *
 * The Scene API texture setters (setWrapS/setOffset/setImage/...) flag the texture's needsUpdate,
 * which dispatches 'update' on the texture but does NOT touch the material. Without this bridge, a
 * texture-only change would be missed whenever the material's batch replays a cached RenderBundle,
 * because the per-draw sync in UberPipeline (updateUniformBuffer + syncMaterialTextureBindings) is
 * skipped for bundled draws. We forward the texture 'update' to the render-bundle invalidation
 * callbacks unconditionally: the setter fired precisely because something changed, and the change
 * (e.g. a re-upload via setImage) may only become observable after initTexture() runs on the next
 * draw - too late for onMaterialChanged's view/sampler comparison to detect it here. Invalidating
 * makes the next frame re-record and pick the change up.
 * @param {THREE.Material} material
 */
function syncTextureUpdateListeners(material) {
  const previous = material.__gpuListenedTextures;

  const current = [];
  for (const { name } of TEXTURE_MAPS) {
    const texture = material[name];
    if (texture && !current.includes(texture)) {
      current.push(texture);
    }
  }

  if (!previous && current.length === 0) {
    return;
  }

  let handler = material.__gpuTextureUpdateHandler;
  if (!handler) {
    handler = material.__gpuTextureUpdateHandler = () => notifyMaterialTextureUpdate(material);
  }

  if (previous) {
    for (const texture of previous) {
      texture.removeEventListener('update', handler);
    }
  }
  for (const texture of current) {
    texture.addEventListener('update', handler);
  }
  material.__gpuListenedTextures = current;
}

function onMaterialChanged(event) {

  const material = event.target;

  // Check if bindGroup needs to be updated.
  // Note that __gpuMaterialUniforms only exists if the material already had textures before.
  // So, if a material was used without textures before and a texture arrived, this check will not help.
  // We detect this case via the mask check below.
  const bindGroupNeedsUpdate = Boolean(material.__gpuMaterialUniforms?.bindGroupNeedsUpdate(material));

  // Check if material mask has changed (e.g. a texture was set on a formerly untextured material)
  const materialTextureMask = material.__gpuUniformsMask & MaterialUniformFlags.TEXTURE_MASK;
  const newMaterialTextureMask = getMaterialTextureMask(material);
  const maskChanged = materialTextureMask !== newMaterialTextureMask;

  // Check if the material change requires a new render pipeline.
  const hasTextures = newMaterialTextureMask !== 0;
  const oldMaterialHash = material.__gpuPipelineHash; // Note that getMateriaHash() already updates this value - so we have to get it first.
  const newMaterialHash = getMaterialHash(material, hasTextures);
  const materialHashChanged = oldMaterialHash !== newMaterialHash;

  if (materialHashChanged || bindGroupNeedsUpdate || maskChanged) {
    // Forward event to all callbacks (one per Renderer instance)

    // Note that this might be called quite often while loading the model.
    // Up to number of textures * number of materials that use the texture.
    // It would be better to disable render bundles while loading large textured models altogether,
    // but determining this and re-enabling render bundles after loading seems complex and invasive.
    for (const callback of materialTextureUpdateCallbacks) {
      callback(event);
    }
  } else {
    // If callbacks are not triggered, just make sure that the uniform buffer values keep up to date.
    // RenderBundles and Pipelines remain valid.
    //
    // Usually, material updates are handled in first initMaterialBindings() call after material modification.
    // However, when using RenderBundles, we don't iterate through individual shapes anymore.
    // So, we have to update the uniform buffer immediately here (if values changed).
    //
    // Remark: It's a bit inconsistent and not ideal to update directly in this case while deferring to next render otherwise.
    //         However, there's currently no simple way where to do instead. Also, when changing
    //         to indirect rendering, this may change soon anyway, so it's not worth major refactorings right now.
    const gpuUniforms = material.__gpuMaterialUniforms;
    if (gpuUniforms) {
      gpuUniforms.updateUniformBuffer(material);
    }
  }
}

export function syncMaterialTextureBindings(material) {
  const gpuUniforms = material.__gpuMaterialUniforms;
  if (!gpuUniforms?.syncTextureBindings(material)) {
    return false;
  }
  notifyMaterialTextureUpdate(material);
  return true;
}

export function initMaterialBindings(device, material, defaultTexture) {

  let mask = getMaterialTextureMask(material);

  if (mask !== 0) {
    if (material.__gpuMaterialUniforms) {
      // Reinitialize from material. In theory, we could also destroy the old MaterialUniforms
      // instance and create a new one. However, the UniformBuffer has always the same size for all
      // materials anyway. If we would destroy and recreate it, this would require invalidating all render bundles
      // that might still use it - even if the textures keep the same. Invalidating render bundles
      // is only done and necessary if the texture mask changed. (see materialTextureUpdateCallback below)
      material.__gpuMaterialUniforms.initFromMaterial(material);
    } else {
      const mu = new MaterialUniforms(device, material, defaultTexture);
      material.__gpuMaterialUniforms = mu;
      material.addEventListener('dispose', materialDisposeHandler);
    }
  }

  // Make sure callbacks are triggered to make sure affected RenderBundles are invalidated
  // whenever bindGroup resources are affected by a change.
  material.addEventListener('update', onMaterialChanged);

  // Also react to changes made directly on the material's textures via the Scene API setters,
  // which flag the texture but not the material. See syncTextureUpdateListeners.
  syncTextureUpdateListeners(material);

  material.__gpuUniformsMask = mask = mask | MaterialUniformFlags.NO_UNIFORMS;
  return mask;
}